import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EvolutionLineChart from '../Graph/EvolutionLineChart';
import DataGridKeyNumber from '../Datagrid/DataGridKeyNumber';
import DataGridCAPPoints from '../Datagrid/DataGridCAPPoints';
import DataGridCAPHelp from '../Datagrid/DataGridCAPHelp';
import NitrogenKeyData from '../Components/NitrogenKeyData';
import TonnageKeyNumber from '../Components/tonnageKeyNumber';
import { createRowBuilder, buildVariationData } from '../utils/RowDataBuilder';
import { formatNumberWithSpaces } from 'utils/index';
import {
  formatCurrency,
  formatPercent,
  formatHa,
  formatM3,
  formatTMSHa,
  formatDecimal,
  formatTonnes,
  formatYield,
} from 'utils/formatters';
import MultiSelectComponent from '../../Component/MultiSelectComponent';
import { sortCultures } from '../utils/cultureSort';

export const buildKeyNumbersColumns = (grossMarginTableData, t) => {
  if (!grossMarginTableData?.campaigns) return [];
  return [t('simulation.common.campaign'), ...grossMarginTableData.campaigns];
};

export const buildGrossMarginRow = (grossMarginTableData, t) => {
  if (!grossMarginTableData?.grossMargin) return null;

  return {
    id: t('simulation.result.keyNumbers.grossMargin'),
    expandable: true,
    data: buildVariationData(grossMarginTableData.grossMargin, formatCurrency),
    children: [
      {
        id: t('simulation.result.keyNumbers.revenue'),
        data: buildVariationData(grossMarginTableData.revenue, formatCurrency),
      },
      {
        id: t('simulation.result.common.charges'),
        invertVariation: true,
        data: buildVariationData(grossMarginTableData.charges, formatCurrency),
      },
    ],
  };
};

const hasConstraint = (configConstraints, name) =>
  configConstraints?.some((c) => c.name === name || c[name] === true);

const sumFromDistribution = (culturesDistribution, field, filterFn) => {
  if (!culturesDistribution?.length) return null;
  const filtered = filterFn ? culturesDistribution.filter(filterFn) : culturesDistribution;
  const total = filtered.reduce((sum, entry) => sum + (entry[field] || 0), 0);
  return total;
};

const findCultureInDistribution = (culturesDistribution, culture) =>
  culturesDistribution?.find((entry) => entry.culture?.id === culture.id);

const filterByCultures = (data, selectedCultureIds) => {
  if (!data || !selectedCultureIds) return data;

  const filterDistribution = (obj) => {
    if (!obj) return obj;
    return {
      ...obj,
      culturesDistribution: obj.culturesDistribution?.filter((entry) =>
        selectedCultureIds.includes(entry.culture?.id),
      ),
    };
  };

  return {
    ...data,
    ...(data.years && {
      years: data.years.map(filterDistribution),
    }),
    ...(data.culturesDistribution && filterDistribution(data)),
  };
};

export const buildOtherRows = (suggest, previous, configConstraints, result, t) => {
  if (!suggest?.years?.length) return [];

  const { years } = suggest;

  const row = createRowBuilder({
    years,
    previous,
    previousCount: previous?.capPoints?.length > 1 ? 2 : 1,
  });

  const rows = [];

  rows.push(
    row.buildRow({
      id: t('simulation.result.keyNumbers.winterCropPercent'),
      fromYear: (year) => year.propWinterCrop,
      format: formatPercent,
      fromPrev: (prev, i) => prev.constraintValues.propWinterCrop?.[i],
    }),
  );

  if (hasConstraint(configConstraints, 'intercropSurface')) {
    rows.push(
      row.buildRow({
        id: t('simulation.result.keyNumbers.intercultureSurfaceTotal'),
        fromYear: (year) => {
          const total = sumFromDistribution(
            year.culturesDistribution,
            'hectares',
            (d) => d.culture?.type === 'intermediate',
          );
          return total > 0 ? total : null;
        },
        format: formatHa,
      }),
    );
  }

  if (result?.constraints?.typeCoProduit.length > 0) {
    const coproduitsTypes = result?.constraints?.typeCoProduit.map((c) => c.displayName);

    const coproduitsChildren = coproduitsTypes.map((displayName) => ({
      id: `${displayName}`,
      data: row.buildData({
        fromYear: (year) => year[displayName],
        format: (val) => (val != null ? `${val} t` : '-'),
        fromPrev: (prev) => prev?.constraintValues?.qttTypeCoProduit?.[displayName],
      }),
    }));

    rows.push({
      id: t('simulation.result.keyNumbers.coProductsTonnage'),
      expandable: true,
      defaultExpanded: true,
      data: row.buildData({
        fromYear: (year) => {
          if (!year.tonnage?.length) return null;
          const total = year.tonnage.reduce((sum, t) => sum + (t.tonnage || 0), 0);
          return total > 0 ? total : null;
        },
        format: (val) => (val != null ? `${val} t` : ''),
      }),
      children: coproduitsChildren,
    });
  }

  if (hasConstraint(configConstraints, 'irrigation')) {
    rows.push({
      id: t('simulation.result.keyNumbers.irrigatedSurface'),
      expandable: true,
      data: row.buildData({ fromYear: (year) => year.surfaceIrrigation, format: formatHa }),
      children: [
        {
          id: t('simulation.result.keyNumbers.irrigationVolume'),
          data: row.buildData({ fromYear: (year) => year.volumeIrrigation, format: formatM3 }),
        },
      ],
    });
  }

  if (hasConstraint(configConstraints, 'extraBiomass')) {
    rows.push(
      row.buildRow({
        id: t('simulation.result.common.averageBiomassInput'),
        fromYear: (year) => year.averageBiomassInput,
        format: formatTMSHa,
        fromPrev: (prev, i) => prev.averageBiomassYearInput?.[i],
      }),
    );
  }

  const hasIFT =
    hasConstraint(configConstraints, 'TFIHerbicideConstraint') ||
    hasConstraint(configConstraints, 'TFIWithoutHerbicideConstraint');

  if (hasIFT) {
    const sumIFT = (herb, noHerb) => {
      if (herb == null && noHerb == null) return null;
      return (herb || 0) + (noHerb || 0);
    };

    rows.push({
      id: t('simulation.result.keyNumbers.averageIFT'),
      expandable: true,
      data: row.buildData({
        fromYear: (year) => sumIFT(year.averageTFIHerbicide, year.averageTFIWithoutHerbicide),
        format: formatDecimal,
        fromPrev: (prev, i) =>
          sumIFT(prev.averageTFIHerbicide?.[i], prev.averageTFIWithoutHerbicide?.[i]),
      }),
      children: [
        {
          id: t('simulation.result.keyNumbers.averageIFTHerbicides'),
          data: row.buildData({
            fromYear: (year) => year.averageTFIHerbicide,
            format: formatDecimal,
            fromPrev: (prev, i) => prev.averageTFIHerbicide?.[i] ?? null,
          }),
        },
        {
          id: t('simulation.result.keyNumbers.averageIFTNonHerbicides'),
          data: row.buildData({
            fromYear: (year) => year.averageTFIWithoutHerbicide,
            format: formatDecimal,
            fromPrev: (prev, i) => prev.averageTFIWithoutHerbicide?.[i] ?? null,
          }),
        },
      ],
    });
  }

  return rows;
};

export const buildTonnageRows = (suggest, previous, cultures, t) => {
  if (!suggest?.years?.length) return [];

  const { years } = suggest;

  const activeCultures =
    cultures
      ?.map((culture, index) => ({ culture, index }))
      .filter(
        ({ culture }) =>
          years.some((year) => {
            const entry = findCultureInDistribution(year.culturesDistribution, culture);
            return entry?.tonnage > 0 || entry?.hectares > 0;
          }) ||
          (() => {
            const entry = findCultureInDistribution(previous?.culturesDistribution, culture);
            return entry?.tonnage > 0 || entry?.hectares > 0;
          })(),
      ) || [];

  const row = createRowBuilder({
    years,
    previous,
    previousCount: previous ? 1 : 0,
    activeCultures,
  });

  const sumCost = (distribution) => {
    const costNitrogen = sumFromDistribution(distribution, 'costNitrogen') || 0;
    const costWithoutNitrogen = sumFromDistribution(distribution, 'costWithoutNitrogen') || 0;
    return costNitrogen + costWithoutNitrogen;
  };

  return [
    {
      id: t('simulation.result.culturesNumbers.tonnage'),
      expandable: true,
      data: row.buildData({
        fromYear: (year) => sumFromDistribution(year.culturesDistribution, 'tonnage'),
        format: formatTonnes,
        fromPrev: (prev) => sumFromDistribution(prev.culturesDistribution, 'tonnage'),
      }),
      children: row.buildCultureChildren({
        fromYear: (year, culture) =>
          findCultureInDistribution(year.culturesDistribution, culture)?.tonnage ?? null,
        format: formatTonnes,
        fromPrev: (prev, culture) =>
          findCultureInDistribution(prev.culturesDistribution, culture)?.tonnage ?? null,
      }),
    },

    {
      id: t('simulation.result.culturesNumbers.averageYield'),
      expandable: true,
      data: '',
      children: row.buildCultureChildren({
        fromYear: (year, culture) =>
          findCultureInDistribution(year.culturesDistribution, culture)?.yieldPerHectare ?? null,
        format: formatYield,
        fromPrev: (prev, culture) =>
          findCultureInDistribution(prev.culturesDistribution, culture)?.yieldPerHectare ?? null,
      }),
    },

    {
      id: t('simulation.result.common.charges'),
      expandable: true,
      data: row.buildData({
        fromYear: (year) => sumCost(year.culturesDistribution),
        format: formatCurrency,
        fromPrev: (prev) => sumCost(prev.culturesDistribution),
      }),
      children: [
        {
          id: t('simulation.result.culturesNumbers.productionCostWithoutNitrogen'),
          expandable: true,
          data: row.buildData({
            fromYear: (year) =>
              sumFromDistribution(year.culturesDistribution, 'costWithoutNitrogen'),
            format: formatCurrency,
            fromPrev: (prev) =>
              sumFromDistribution(prev.culturesDistribution, 'costWithoutNitrogen'),
          }),
          children: row.buildCultureChildren({
            fromYear: (year, culture) =>
              findCultureInDistribution(year.culturesDistribution, culture)?.costWithoutNitrogen ??
              null,
            format: formatCurrency,
            fromPrev: (prev, culture) =>
              findCultureInDistribution(prev.culturesDistribution, culture)?.costWithoutNitrogen ??
              null,
          }),
        },
        {
          id: t('simulation.result.culturesNumbers.nitrogenCost'),
          expandable: true,
          data: row.buildData({
            fromYear: (year) => sumFromDistribution(year.culturesDistribution, 'costNitrogen'),
            format: formatCurrency,
            fromPrev: (prev) => sumFromDistribution(prev.culturesDistribution, 'costNitrogen'),
          }),
          children: row.buildCultureChildren({
            fromYear: (year, culture) =>
              findCultureInDistribution(year.culturesDistribution, culture)?.costNitrogen ?? null,
            format: formatCurrency,
            fromPrev: (prev, culture) =>
              findCultureInDistribution(prev.culturesDistribution, culture)?.costNitrogen ?? null,
          }),
        },
      ],
    },
  ];
};

const buildPacTable = (suggest, previous, forecastCampaign) => {
  if (!suggest?.years?.[0]?.capPoints) return [];

  const pacTable = suggest.years[0].capPoints.map((m) => ({ categorie: m.categorie, data: [] }));
  pacTable.push({ categorie: 'Total', data: [] });

  if (previous?.capPoints) {
    const prevPoints = Array.isArray(previous.capPoints[0])
      ? previous.capPoints
      : [previous.capPoints];

    prevPoints.forEach((pointsArray) => {
      let total = 0;
      pacTable.forEach((row) => {
        if (row.categorie === 'Total') {
          row.data.push({ value: total });
        } else {
          const found = pointsArray?.find((p) => p.categorie === row.categorie);
          const value = found?.points || 0;
          row.data.push({ value });
          total += value;
        }
      });
    });
  }

  suggest.years.forEach((year) => {
    let total = 0;
    year.capPoints?.forEach((p) => {
      const row = pacTable.find((r) => r.categorie === p.categorie);
      if (row) {
        row.data.push({ value: p.points });
        total += p.points;
      }
    });
    pacTable.find((r) => r.categorie === 'Total')?.data.push({ value: total });
  });

  return pacTable;
};

const buildPacColumns = (suggest, previous, forecastCampaign, t) => {
  const hasTwoPrevious = previous?.capPoints?.length > 1;
  const prevColumns = hasTwoPrevious
    ? [
        t('simulation.common.campaign'),
        `${forecastCampaign.startYear - 2} - ${forecastCampaign.endYear - 2}`,
        `${forecastCampaign.startYear - 1} - ${forecastCampaign.endYear - 1}`,
        `${forecastCampaign.startYear} - ${forecastCampaign.endYear}`,
      ]
    : [
        t('simulation.common.campaign'),
        `${forecastCampaign.startYear - 1} - ${forecastCampaign.endYear - 1}`,
        `${forecastCampaign.startYear} - ${forecastCampaign.endYear}`,
      ];

  const yearColumns = Array(suggest?.years?.length - 1 || 0)
    .fill()
    .map((_, i) => `${i + forecastCampaign.startYear + 1}-${i + forecastCampaign.endYear + 1}`);

  return [...prevColumns, ...yearColumns];
};

const buildPacHelps = (capHelps) => {
  if (!capHelps?.length) return [];
  return capHelps.map((elt) => `${formatNumberWithSpaces(elt)} €`);
};

const SectionKeyNumber = ({
  result,
  previous,
  forecastCampaign,
  suggest,
  grossMarginTableData,
  lineChartData,
  configConstraints,
  cultures,
  capHelps,
}) => {
  const { t } = useTranslation();
  const [pacOpen, setPacOpen] = useState(false);
  const [selectedCultureIds, setSelectedCultureIds] = useState(null);

  const toggleCultureId = (id) => {
    if (!id) {
      setSelectedCultureIds([]);
      return;
    }

    if (Array.isArray(id)) {
      setSelectedCultureIds(id.map((item) => item.id));
      return;
    }

    setSelectedCultureIds((current) => {
      const selected = current !== null ? current : allCultureIds;
      if (selected.includes(id)) return selected.filter((i) => i !== id);
      return [...selected, id];
    });
  };

  const availableCultures = useMemo(() => {
    const cultureMap = new Map();

    suggest?.years?.forEach((year) =>
      year.culturesDistribution?.forEach((d) => {
        if ((d.tonnage > 0 || d.hectares > 0) && d.culture?.id) {
          cultureMap.set(d.culture.id, d.culture);
        }
      }),
    );

    previous?.culturesDistribution?.forEach((d) => {
      if ((d.tonnage > 0 || d.hectares > 0) && d.culture?.id) {
        cultureMap.set(d.culture.id, d.culture);
      }
    });

    return sortCultures([...cultureMap.values()]);
  }, [suggest, previous]);

  const allCultureIds = useMemo(() => availableCultures.map((c) => c.id), [availableCultures]);

  const effectiveSelectedIds = useMemo(
    () => selectedCultureIds ?? allCultureIds,
    [selectedCultureIds, allCultureIds],
  );
  const filteredSuggest = useMemo(
    () => filterByCultures(suggest, effectiveSelectedIds),
    [suggest, effectiveSelectedIds],
  );
  const filteredPrevious = useMemo(
    () => filterByCultures(previous, effectiveSelectedIds),
    [previous, effectiveSelectedIds],
  );

  const columns = useMemo(
    () => buildKeyNumbersColumns(grossMarginTableData, t),
    [grossMarginTableData, t],
  );

  const allRows = useMemo(() => {
    const grossMarginRow = buildGrossMarginRow(grossMarginTableData, t);
    const otherRows = buildOtherRows(suggest, previous, configConstraints, result, t);
    return [...(grossMarginRow ? [grossMarginRow] : []), ...otherRows];
  }, [grossMarginTableData, suggest, previous, configConstraints, result, t]);

  const tonnageRows = useMemo(
    () => buildTonnageRows(filteredSuggest, filteredPrevious, cultures, t),
    [filteredSuggest, filteredPrevious, cultures, t],
  );

  const showNitrogen = hasConstraint(configConstraints, 'nitrogen');

  const pacRows = useMemo(
    () => buildPacTable(suggest, previous, forecastCampaign),
    [suggest, previous, forecastCampaign],
  );
  const pacColumns = useMemo(
    () => buildPacColumns(suggest, previous, forecastCampaign, t),
    [suggest, previous, forecastCampaign, t],
  );
  const pacHelpsRows = useMemo(() => buildPacHelps(capHelps), [capHelps]);

  const showPac = hasConstraint(configConstraints, 'activateCAPCategory');
  const hasPacData = showPac && (pacRows.length > 0 || pacHelpsRows.length > 0);

  return (
    <>
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">{t('simulation.result.common.keyNumbersTitle')}</h2>
        </div>
        <DataGridKeyNumber rows={allRows} columns={columns} />
        <EvolutionLineChart data={lineChartData} />
      </section>

      {tonnageRows.length > 0 && (
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">{t('simulation.result.common.culturesNumbersTitle')}</h2>
            <MultiSelectComponent
              items={availableCultures}
              selectedIds={effectiveSelectedIds}
              onToggle={toggleCultureId}
              placeholder={t('simulation.result.culturesNumbers.filterCultures')}
              renderItemLabel={(c) => c.name}
              showTags={false}
              fullWidth={false}
              selectAll={true}
            />
          </div>
          <TonnageKeyNumber rows={tonnageRows} columns={columns} />
        </section>
      )}

      {showNitrogen && (
        <NitrogenKeyData
          suggest={suggest}
          forecastCampaign={forecastCampaign}
          cultures={cultures}
          previous={previous}
        />
      )}

      {hasPacData && (
        <section className="section">
          <div
            className="section-header section-header clickable"
            onClick={() => setPacOpen(!pacOpen)}
          >
            <h2 className="section-title">{t('simulation.result.pac.title')}</h2>
            {pacOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
          {pacOpen && (
            <div className="section-collapsible">
              {pacRows.length > 0 && <DataGridCAPPoints rows={pacRows} columns={pacColumns} />}
              {pacHelpsRows.length > 0 && <DataGridCAPHelp rows={pacHelpsRows} />}
            </div>
          )}
        </section>
      )}
    </>
  );
};

export default SectionKeyNumber;
